<template>
  <div class="aircraft-info">
    <div class="main-container">
      <div aria-label="Breadcrumb" class="breadcrumb-container">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ href }">
              <a :href="href" @click.prevent="onClickSettings">設定トップ</a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">機体情報設定</span>
          </li>
        </ol>
      </div>
      <Form @submit="handleSave" v-slot="{ meta }">
        <div class="container">
          <h2 class="config-headline">
            <img
              src="../assets/images/icon/icon_drone_orange.svg"
              alt=""
              class="icon-config-headline"
            />
            機体情報設定
          </h2>
          <ul class="aircraft" v-if="droneInfo">
            <li class="aircraft-category">
              <h3>風速</h3>
              <div class="inputs">
                <div class="input-unit">
                  <label for="" class="label-s">注意</label>
                  <NumberField
                    name="windCaution"
                    v-model="droneInfo.wind_caution"
                    rules="between:0.0,99.9,1"
                    :decimals="1"
                    inputClass="num"
                    :externalError="!!windThresholdError"
                    @blur="() => validateWindThreshold()"
                  />
                  <span class="unit"><span class="unit-en">m/s</span>以上</span>
                  <ErrorMessageBox name="windCaution" />
                  <ErrorMessageBox v-if="windThresholdError" :message="windThresholdError" />
                </div>
                <div class="input-unit">
                  <label for="" class="label-s">警戒</label>
                  <NumberField
                    name="windWarning"
                    v-model="droneInfo.wind_warning"
                    rules="between:0.0,99.9,1"
                    :decimals="1"
                    inputClass="num"
                    @blur="() => validateWindThreshold()"
                  />
                  <span class="unit"><span class="unit-en">m/s</span>以上</span>
                  <ErrorMessageBox name="windWarning" />
                </div>
              </div>
            </li>
            <li class="aircraft-category">
              <h3>降水</h3>
              <div class="inputs">
                <div class="input-unit">
                  <label for="" class="label-s">注意</label>
                  <NumberField
                    name="rainCaution"
                    v-model="droneInfo.rain_caution"
                    rules="between:0.0,999.9,1"
                    :decimals="1"
                    inputClass="num"
                    :externalError="!!rainThresholdError"
                    @blur="validateRainThreshold"
                  />
                  <span class="unit"><span class="unit-en">mm/h</span>以上</span>
                  <ErrorMessageBox name="rainCaution" />
                  <ErrorMessageBox v-if="rainThresholdError" :message="rainThresholdError" />
                </div>
                <div class="input-unit">
                  <label for="" class="label-s">警戒</label>
                  <NumberField
                    name="rainWarning"
                    v-model="droneInfo.rain_warning"
                    rules="between:0.0,999.9"
                    :decimals="1"
                    inputClass="num"
                    @blur="() => validateRainThreshold()"
                  />
                  <span class="unit"><span class="unit-en">mm/h</span>以上</span>
                  <ErrorMessageBox name="rainWarning" />
                </div>
              </div>
            </li>
            <li class="aircraft-category">
              <h3>降雪</h3>
              <div class="inputs">
                <div class="input-unit">
                  <label for="" class="label-s">注意</label>
                  <NumberField
                    name="snowCaution"
                    v-model="droneInfo.snow_caution"
                    rules="between:0,99"
                    :decimals="0"
                    inputClass="num"
                    :externalError="!!snowThresholdError"
                    @blur="() => validateSnowThreshold()"
                  />
                  <span class="unit"><span class="unit-en">cm/h</span>以上</span>
                  <ErrorMessageBox name="snowCaution" />
                  <ErrorMessageBox v-if="snowThresholdError" :message="snowThresholdError" />
                </div>
                <div class="input-unit">
                  <label for="" class="label-s">警戒</label>
                  <NumberField
                    name="snowWarning"
                    v-model="droneInfo.snow_warning"
                    rules="between:0,99"
                    :decimals="0"
                    inputClass="num"
                    @blur="() => validateSnowThreshold()"
                  />
                  <span class="unit"><span class="unit-en">cm/h</span>以上</span>
                  <ErrorMessageBox name="snowWarning" />
                </div>
              </div>
            </li>
            <li class="aircraft-category">
              <h3>気温</h3>
              <div class="inputs">
                <div class="input-unit">
                  <label for="" class="label-s">注意</label>
                  <NumberField
                    name="heatCaution"
                    v-model="droneInfo.heat_caution"
                    rules="between:-99.9,99.9,1"
                    :decimals="1"
                    inputClass="num"
                    :inputmode="'text'"
                    :externalError="!!heatThresholdError || !!cautionThresholdError"
                    @blur="validateTempThresholdWrapper"
                  />
                  <span class="unit"><span class="unit-en">℃</span>以上</span>
                  <ErrorMessageBox name="heatCaution" />
                  <ErrorMessageBox v-if="heatThresholdError" :message="heatThresholdError" />
                  <ErrorMessageBox v-if="cautionThresholdError" :message="cautionThresholdError" />
                </div>
                <div class="input-unit">
                  <label for="" class="label-s">警戒</label>
                  <NumberField
                    name="heatWarning"
                    v-model="droneInfo.heat_warning"
                    rules="between:-99.9,99.9,1"
                    :decimals="1"
                    inputClass="num"
                    :inputmode="'text'"
                    :externalError="!!warningThresholdError"
                    @blur="validateTempThresholdWrapper"
                  />
                  <span class="unit"><span class="unit-en">℃</span>以上</span>
                  <ErrorMessageBox name="heatWarning" />
                  <ErrorMessageBox v-if="warningThresholdError" :message="warningThresholdError" />
                </div>
              </div>
              <div class="inputs">
                <div class="input-unit">
                  <label for="" class="label-s">注意</label>
                  <NumberField
                    name="coldCaution"
                    v-model="droneInfo.cold_caution"
                    rules="between:-99.9,99.9,1"
                    :decimals="1"
                    inputClass="num"
                    :inputmode="'text'"
                    :externalError="!!coldThresholdError"
                    @blur="validateTempThresholdWrapper"
                  />
                  <span class="unit"><span class="unit-en">℃</span>以下</span>
                  <ErrorMessageBox name="coldCaution" />
                  <ErrorMessageBox v-if="coldThresholdError" :message="coldThresholdError" />
                </div>
                <div class="input-unit">
                  <label for="" class="label-s">警戒</label>
                  <NumberField
                    name="coldWarning"
                    v-model="droneInfo.cold_warning"
                    rules="between:-99.9,99.9,1"
                    :decimals="1"
                    inputClass="num"
                    :inputmode="'text'"
                    @blur="validateTempThresholdWrapper"
                  />
                  <span class="unit"><span class="unit-en">℃</span>以下</span>
                  <ErrorMessageBox name="coldWarning" />
                </div>
              </div>
            </li>
          </ul>
          <ul class="sub-aircraft">
            <li>
              <h3>機体名</h3>
              <InputText
                name="droneName"
                rules="max_length:20"
                v-model="droneInfo.drone_name"
                type="text"
                :maxlength="20"
                class="aircraft-name"
              />
              <ErrorMessageBox name="droneName" />
            </li>
            <li>
              <h3>コメント</h3>
              <TextareaCounter
                name="comment"
                rules="max_length:100"
                :rows="3"
                :limit="100"
                v-model="droneInfo.comment"
              />
              <ErrorMessageBox name="comment" />
            </li>
          </ul>

          <div class="backspace">
            <button class="button-secondary" @click="onClickClearAll" type="button">
              <span>
                <img src="../assets/images/icon/icon_backspace.svg" alt="" />
              </span>
              入力内容をすべてクリア
            </button>
          </div>
        </div>
        <div class="config-action">
          <div class="action-button-group">
            <button
              class="button-secondary alert"
              @click="onClickUndo"
              :disabled="isSaving"
              type="button"
            >
              <span>
                <img src="../assets/images/icon/icon_undo.svg" alt="" />
              </span>
              リセット
            </button>
            <button
              type="button"
              class="button-primary"
              :class="{ 'is-disabled': isSaving || !meta.valid || hasCrossError }"
              :disabled="isSaving || !meta.valid || hasCrossError"
              @click="handleSave"
            >
              設定を保存する
            </button>
          </div>
        </div>
      </Form>
      <div aria-label="Breadcrumb" class="breadcrumb-container-under">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <RouterLink to="/settings" custom v-slot="{ href }">
              <a :href="href" @click.prevent="onClickSettings">設定トップ</a>
            </RouterLink>
            <span class="breadcrumb-separator"></span>
          </li>
          <li class="breadcrumb-item">
            <span aria-current="page">機体情報設定</span>
          </li>
        </ol>
      </div>
      <UndoAlertModal
        :isOpen="confirmModal.visible"
        :title="confirmModal.title"
        :message="confirmModal.message"
        :cancelText="confirmModal.cancelText"
        :confirmText="confirmModal.confirmText"
        @cancel="confirmModal.cancel()"
        @confirm="confirmModal.confirm()"
      />
      <Toast />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { Form } from 'vee-validate'
import NumberField from '@/components/form/NumberField.vue'
import InputText from '@/components/form/InputText.vue'
import TextareaCounter from '@/components/form/TextareaCounter.vue'
import ErrorMessageBox from '@/components/form/ErrorMessageBox.vue'
import Toast from '@/components/common/Toast.vue'
import UndoAlertModal from '@/components/modal/UndoAlertModal.vue'
import { useDroneInfoLogic } from '@/composables/DroneInfoLogic'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { getFieldMessage } from '@/utils/message'
import { useConfirmBeforeLeave } from '@/composables/useConfirmBeforeLeave'
import { useConfirmDiscard } from '@/composables/useConfirmDiscard'

const router = useRouter()

const confirmModal = useConfirmModalStore()
const { droneInfo, clearForm, loadDroneInfo, isDirty, isSaving, update } = useDroneInfoLogic()

// 戻るボタンの処理
useConfirmBeforeLeave(isDirty)

// 画面表示時
onMounted(async () => {
  await loadDroneInfo()
})

// 「設定トップ」リンク押下
async function onClickSettings() {
  if (isDirty.value) {
    await confirmDiscardThen(() => {
      isDirty.value = false
      router.push('/settings')
    })
  } else {
    await router.push('/settings')
  }
}

// 「設定を保存する」ボタン押下
async function onSubmit() {
  if (isSaving.value) return
  await update()
}

// エラーをクリア
function clearCrossErrors() {
  windThresholdError.value = undefined
  rainThresholdError.value = undefined
  snowThresholdError.value = undefined
  heatThresholdError.value = undefined
  coldThresholdError.value = undefined
  cautionThresholdError.value = undefined
  warningThresholdError.value = undefined
}

// 「入力内容をすべてクリア」ボタン押下
async function onClickClearAll() {
  clearForm()
  clearCrossErrors()
}

// 「リセット」ボタン押下
async function onClickUndo() {
  await confirmDiscardThen(async () => {
    await loadDroneInfo()
    clearCrossErrors()
  })
}

import type { NavigationFailure } from 'vue-router'

type DiscardActionResult = void | Promise<void | NavigationFailure | undefined>

async function confirmDiscardThen(action: () => DiscardActionResult) {
  const { confirmDiscard } = useConfirmDiscard()

  const ok = await confirmDiscard()
  if (ok) {
    await action()
  }
}

const windThresholdError = ref<string | undefined>(undefined)
const rainThresholdError = ref<string | undefined>(undefined)
const snowThresholdError = ref<string | undefined>(undefined)
const heatThresholdError = ref<string | undefined>(undefined)
const coldThresholdError = ref<string | undefined>(undefined)
const cautionThresholdError = ref<string | undefined>(undefined)
const warningThresholdError = ref<string | undefined>(undefined)

/**
 * 大小相関チェック
 * @param cautionValue 注意値
 * @param warningValue 警戒値
 * @param errorRef エラーメッセージ用 ref
 * @param reverse "以下"で判断する場合 true
 */
function validateThreshold(
  cautionValue: number | string | undefined | null,
  warningValue: number | string | undefined | null,
  errorRef: typeof windThresholdError,
  reverse = false,
) {
  setTimeout(() => {
    const cRaw = cautionValue
    const wRaw = warningValue
    const c = cRaw === '' || cRaw === null || cRaw === undefined ? NaN : parseFloat(String(cRaw))
    const w = wRaw === '' || wRaw === null || wRaw === undefined ? NaN : parseFloat(String(wRaw))

    // どちらかが未入力・数値でない場合はエラークリア
    if (Number.isNaN(c) || Number.isNaN(w)) {
      errorRef.value = undefined
      return
    }

    if (reverse) {
      // "以下"かつ 注意 < 警戒 の場合
      if (c < w) {
        errorRef.value = getFieldMessage('cautionunder910')
        return
      }
    } else if (c > w) {
      // "以上"かつ 注意 > 警戒 の場合
      errorRef.value = getFieldMessage('cautionover909')
      return
    }

    // それ以外はエラーなし
    errorRef.value = undefined
  }, 0)
}

/**
 * 気温用：耐寒 > 耐暑 でエラー
 */
function validateHeatColdThreshold(
  heatValue: number | string | undefined | null,
  coldValue: number | string | undefined | null,
  errorRef: typeof heatThresholdError | typeof coldThresholdError,
) {
  setTimeout(() => {
    const h =
      heatValue === '' || heatValue === null || heatValue === undefined
        ? NaN
        : parseFloat(String(heatValue))
    const c =
      coldValue === '' || coldValue === null || coldValue === undefined
        ? NaN
        : parseFloat(String(coldValue))

    if (Number.isNaN(h) || Number.isNaN(c)) {
      errorRef.value = undefined
      return
    }

    if (c > h) {
      errorRef.value = getFieldMessage('hotcautionover911')
      return
    }

    errorRef.value = undefined
  }, 0)
}

function validateWindThreshold() {
  validateThreshold(droneInfo.wind_caution, droneInfo.wind_warning, windThresholdError)
}

function validateRainThreshold() {
  validateThreshold(droneInfo.rain_caution, droneInfo.rain_warning, rainThresholdError)
}

function validateSnowThreshold() {
  validateThreshold(droneInfo.snow_caution, droneInfo.snow_warning, snowThresholdError)
}

function validateTempThresholdWrapper() {
  validateThreshold(droneInfo.heat_caution, droneInfo.heat_warning, heatThresholdError)
  validateThreshold(droneInfo.cold_caution, droneInfo.cold_warning, coldThresholdError, true)
  validateHeatColdThreshold(droneInfo.heat_caution, droneInfo.cold_caution, cautionThresholdError)
  validateHeatColdThreshold(droneInfo.heat_warning, droneInfo.cold_warning, warningThresholdError)
}

const hasCrossError = computed(
  () =>
    !!windThresholdError.value ||
    !!rainThresholdError.value ||
    !!snowThresholdError.value ||
    !!heatThresholdError.value ||
    !!coldThresholdError.value ||
    !!cautionThresholdError.value ||
    !!warningThresholdError.value,
)

async function handleSave() {
  // すべてのクロスバリデーションを明示的に実行
  validateWindThreshold()
  validateRainThreshold()
  validateSnowThreshold()
  validateTempThresholdWrapper()

  // setTimeoutで非同期になっているので、少し待つ
  await new Promise((resolve) => setTimeout(resolve, 0))

  if (hasCrossError.value) return
  await onSubmit()
}
</script>
